import { useCallback, useMemo, useRef, useState } from "react";
import DynamicForm, { DynamicFormRef, setCurrencySymbolForMoneyInputs } from "@mavichpe/dynamic-forms";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faPenToSquare, faPlus, faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";
import cx from "classnames";
import styles from "./style.module.scss";
import buildDocumentOtherChargesFormConfig from "./form";
import {
    MAX_OTHER_CHARGES,
    StoredOtherCharge,
    createEmptyOtherCharge,
    fromOtherChargeFormData,
    generateOtherChargeId,
    resolveChargeAmount,
    serviceTaxCharge,
    toOtherChargeFormData,
} from "@utils/DocumentOtherCharges";
import { OtherChargeFormData } from "@/formTypes/OtherChargeFormDataType";
import {
    COLON_CURRENCY_ID,
    CURRENCIES_SYMBOLS,
    OTHER_CHARGE_TYPE_OTHERS,
    getOtherChargeTypeLabel,
} from "@utils/Constants";
import { formatMoneyAmount } from "@utils/EDocument";
import { showAlert } from "@utils/Alert";
import {
    resolveTaxpayer,
    TAXPAYER_NOT_REGISTERED_MESSAGE,
    TAXPAYER_UNVERIFIED_MESSAGE,
} from "@utils/taxpayerRegistry";

type DocumentOtherChargesEditorProps = {
    value: StoredOtherCharge[];
    onChange: (next: StoredOtherCharge[]) => void;
    /** Venta neta del documento: base sobre la que se resuelve un cargo porcentual. */
    base: number;
    readonly?: boolean;
    maxItems?: number;
    currencySymbol?: string;
};

/** Atajos del flujo de mostrador: crean un cargo completo de un click. */
const PRESETS: { label: string; build: () => StoredOtherCharge }[] = [
    { label: "Servicio 10%", build: () => serviceTaxCharge(10) },
    { label: "Timbre Cruz Roja", build: () => createEmptyOtherCharge("02") },
];

const DocumentOtherChargesEditor = ({
    value,
    onChange,
    base,
    readonly = false,
    maxItems = MAX_OTHER_CHARGES,
    currencySymbol = "",
}: DocumentOtherChargesEditorProps) => {
    const formRef = useRef<DynamicFormRef>(null);
    const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    /** Espejo del borrador, solo para la vista previa del monto porcentual. */
    const [draftPreview, setDraftPreview] = useState<Partial<OtherChargeFormData>>({});
    /** Mismo espejo pero sincrono: `onInputChange` corre antes de que el estado se aplique. */
    const draftRef = useRef<Partial<OtherChargeFormData>>({});

    const trackDraft = useCallback((formState: Partial<OtherChargeFormData>) => {
        draftRef.current = formState;
        setDraftPreview(formState);
    }, []);

    /**
     * Mantiene el espejo del borrador y precarga el detalle con el nombre del tipo elegido.
     *
     * El espejo se lleva desde aqui y no desde `onStateChange`, porque dynamic-forms solo
     * dispara ese callback cuando el formulario corre con `useExternalState`, que no es el
     * caso: con `onStateChange` la vista previa del porcentaje nunca llegaba a pintarse.
     * `onInputChange`, en cambio, corre en cada cambio de campo.
     *
     * El detalle sigue siendo editable: si el usuario ya lo escribio a mano no se pisa, solo
     * se actualiza mientras siga siendo el texto que puso el sistema.
     */
    const handleInputChange = useCallback((name: keyof OtherChargeFormData, value: unknown) => {
        const previous = draftRef.current;
        trackDraft({ ...previous, [name]: value });

        if (name !== "tipoDocumentoOC") return;
        const detalleActual = `${previous.detalle || ""}`;
        const detalleEraAutomatico =
            !detalleActual || detalleActual === getOtherChargeTypeLabel(`${previous.tipoDocumentoOC || ""}`);
        if (detalleEraAutomatico) {
            // Diferido: `onInputChange` corre ANTES de que dynamic-forms aplique el cambio
            // de tipo, y un setFormValue sincrono lo pisaria con el estado anterior.
            setTimeout(
                () => formRef.current?.setFormValue("detalle", getOtherChargeTypeLabel(`${value}`), "String"),
                0,
            );
        }
    }, [trackDraft]);

    const limitReached = value.length >= maxItems;
    /**
     * El offcanvas manda la moneda del documento; el fallback solo cubre el render previo a
     * que el slice de moneda cargue. Sin simbolo, el input `money` de dynamic-forms pinta un
     * icono de dolar, que es justo lo que no queremos en una factura en colones.
     */
    const symbol = currencySymbol || CURRENCIES_SYMBOLS[COLON_CURRENCY_ID];
    const money = (amount: number): string => `${symbol} ${formatMoneyAmount(amount)}`;

    /**
     * Consulta el padron de Tributacion con lo que hay en el borrador y siembra el nombre
     * oficial del tercero. Sirve de atajo y de verificacion explicita: la misma comprobacion
     * corre sola al guardar el cargo, via la validacion del campo.
     */
    const verifyIdentification = useCallback(async () => {
        const { identificacionTipo, identificacionNumero } = draftRef.current;
        const taxpayer = await resolveTaxpayer(identificacionTipo, identificacionNumero);

        if (taxpayer.status === "not_registered") {
            showAlert("danger", TAXPAYER_NOT_REGISTERED_MESSAGE);
            return;
        }
        if (taxpayer.status === "unknown") {
            showAlert("warning", TAXPAYER_UNVERIFIED_MESSAGE);
            return;
        }

        showAlert("success", `Identificación inscrita ante Tributación: ${taxpayer.nombre || ""}`);
        if (taxpayer.nombre) {
            // Mismo orden que `handleInputChange`: primero el espejo y despues, diferido, el
            // campo. Al reves el re-render que provoca `trackDraft` reinicializa el form y se
            // come el valor recien sembrado.
            trackDraft({ ...draftRef.current, nombreTercero: taxpayer.nombre });
            setTimeout(() => formRef.current?.setFormValue("nombreTercero", taxpayer.nombre, "String"), 0);
        }
    }, [trackDraft]);

    /** El simbolo tiene que llegar al input del monto, no solo a los textos del editor. */
    const formConfig = useMemo(
        () => setCurrencySymbolForMoneyInputs(
            buildDocumentOtherChargesFormConfig({ onVerifyIdentification: verifyIdentification }),
            symbol,
        ),
        [symbol, verifyIdentification],
    );

    const closeForm = () => {
        setIsFormOpen(false);
        setEditingId(null);
        trackDraft({});
    };

    const openForm = (charge: StoredOtherCharge) => {
        const formData = toOtherChargeFormData(charge);
        setEditingId(charge.id);
        trackDraft(formData);
        setIsFormOpen(true);
        // El formulario se monta en este mismo render, asi que sembrarlo hay que diferirlo.
        setTimeout(() => formRef.current?.resetFormValue(formData), 0);
    };

    const handleSubmit = (formData: OtherChargeFormData) => {
        const id = editingId || generateOtherChargeId();
        const existing = value.find((c) => c.id === id);
        const charge = fromOtherChargeFormData(formData, id);
        const resolved: StoredOtherCharge = {
            ...charge,
            // `source` no pasa por el formulario, asi que editar un cargo de envio no debe
            // borrar su marca de origen. Cambiar el tipo a algo distinto de "04" ya lo
            // descalifica como envio por si solo.
            source: existing?.source,
            montoCargo: resolveChargeAmount(charge, base),
        };
        const exists = value.some((c) => c.id === id);
        onChange(exists ? value.map((c) => (c.id === id ? resolved : c)) : [...value, resolved]);
        closeForm();
    };

    const handleDelete = (chargeId: string) => {
        onChange(value.filter((charge) => charge.id !== chargeId));
    };

    const renderChargeCard = (charge: StoredOtherCharge) => (
        <div key={charge.id} className={styles.chargeCard} data-testid={`other-charge-card-${charge.id}`}>
            <div className={styles.chargeCardMeta}>
                <div className={styles.metaPrimary}>
                    <span className={styles.badge}>{charge.tipoDocumentoOC}</span>
                    <span className={styles.metaTipo}>
                        {charge.tipoDocumentoOC === OTHER_CHARGE_TYPE_OTHERS && charge.tipoDocumentoOTROS
                            ? charge.tipoDocumentoOTROS
                            : getOtherChargeTypeLabel(charge.tipoDocumentoOC)}
                    </span>
                    {charge.mode === "percent" && (
                        <span className={cx(styles.badge, styles.badgePercent)}>{charge.porcentajeOC}%</span>
                    )}
                </div>
                <div className={styles.metaSecondary}>{charge.detalle}</div>
                {charge.nombreTercero && (
                    <div className={styles.metaTercero}>
                        Tercero: {charge.nombreTercero} ({charge.identificacionTercero?.numero})
                    </div>
                )}
            </div>
            <div className={styles.chargeAmount}>{money(resolveChargeAmount(charge, base))}</div>
            {!readonly && (
                <div className={styles.chargeActions}>
                    <Button
                        size="sm"
                        color="primary"
                        type="button"
                        data-testid={`other-charge-edit-${charge.id}`}
                        onClick={() => openForm(charge)}
                        title="Editar cargo"
                    >
                        <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                    <Button
                        size="sm"
                        color="danger"
                        type="button"
                        data-testid={`other-charge-delete-${charge.id}`}
                        onClick={() => handleDelete(`${charge.id}`)}
                        title="Eliminar cargo"
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                </div>
            )}
        </div>
    );

    /**
     * El caso de uso #1 es porcentual, y obligar a calcular a mano sobre la venta neta es
     * justo lo que hacia que la gente usara el atajo de la linea "propina". La vista previa
     * vive fuera del DynamicForm porque no es un campo: es el resultado del calculo.
     */
    const renderPercentPreview = () => {
        if (`${draftPreview.mode}` !== "percent") return null;
        const porcentaje = Number(draftPreview.porcentajeOC || 0);
        return (
            <div className={styles.fieldHint} data-testid="other-charge-preview">
                {porcentaje}% de {money(base)} = {money((base * porcentaje) / 100)}
            </div>
        );
    };

    return (
        <div className={styles.editor}>
            <div className={styles.headerRow}>
                <span className={styles.counter} data-testid="other-charges-counter">
                    {value.length}/{maxItems} cargos
                </span>
                {!readonly && (
                    <Button
                        color="success"
                        type="button"
                        data-testid="other-charge-add"
                        onClick={() => openForm(createEmptyOtherCharge(""))}
                        disabled={limitReached || isFormOpen}
                        title={limitReached ? `Máximo ${maxItems} cargos` : undefined}
                    >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Agregar cargo
                    </Button>
                )}
            </div>

            {!readonly && !isFormOpen && !limitReached && (
                <div className={styles.presetsRow}>
                    {PRESETS.map((preset) => (
                        <Button
                            key={preset.label}
                            size="sm"
                            color="light"
                            type="button"
                            className={styles.presetButton}
                            data-testid={`other-charge-preset-${preset.build().tipoDocumentoOC}`}
                            onClick={() => openForm(preset.build())}
                        >
                            <FontAwesomeIcon icon={faBolt} className="me-2" />
                            {preset.label}
                        </Button>
                    ))}
                </div>
            )}

            {readonly && (
                <div className={styles.readonlyNotice}>
                    El documento ya fue emitido. Los cargos son solo de lectura.
                </div>
            )}

            {value.length === 0 ? (
                <div className={styles.emptyState}>
                    No hay otros cargos configurados para este documento.
                </div>
            ) : (
                value.map(renderChargeCard)
            )}

            {!readonly && isFormOpen && (
                <div className={styles.formCard}>
                    {/*
                      * `innerForm` es obligatorio aqui: el editor se monta dentro del
                      * offcanvas del documento y un <form> anidado haria que Enter enviara
                      * el documento en vez de agregar el cargo.
                      */}
                    <DynamicForm<OtherChargeFormData>
                        ref={formRef}
                        innerForm={true}
                        formConfig={formConfig}
                        onInputChange={handleInputChange}
                        onSubmit={handleSubmit}
                    />
                    {renderPercentPreview()}
                    <div className={styles.formActions}>
                        <Button color="secondary" type="button" onClick={closeForm}>
                            <FontAwesomeIcon icon={faXmark} className="me-2" />
                            Cancelar
                        </Button>
                        <Button
                            color="primary"
                            type="button"
                            data-testid="other-charge-save"
                            onClick={() => formRef.current?.triggerFormSubmit()}
                        >
                            {editingId && value.some((c) => c.id === editingId) ? "Actualizar" : "Agregar"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentOtherChargesEditor;
